import { Injectable, inject } from '@angular/core';
import { Observable, map, shareReplay, catchError, of } from 'rxjs';
import { BaseTwcService } from './twc-base.data.service';
import { TwcWebTileInfo } from './twc-web-tile-info.model';
import { TwcParams, TwcResponseData } from './twc.data.types';
import { TwcTimeValidationService } from '../twc-time-validation.service';
import { NGXLogger } from 'ngx-logger';

export const WEB_TILE_ROOT_URL = 'https://api.weather.com/v3/TileServer'; // WebTile only

@Injectable({ providedIn: 'root' })
export class TwcWebTileDataService extends BaseTwcService<TwcResponseData> {
  private readonly _timeValidationService = inject(TwcTimeValidationService);
  private readonly _logger = inject(NGXLogger);

  getTwcData$({
    twcProduct,
    flightLevelValues,
    updatedTimeInSeconds
  }: TwcParams): Observable<TwcResponseData> {
    const params = this.getHttpParams({
      apiKey: this.twcApiKey ?? '',
      filter: twcProduct.defaultProductId
    });

    return this.httpClient
      .get<TwcWebTileInfo>(`${WEB_TILE_ROOT_URL}/series/productSet/boeing`, {
        params
      })
      .pipe(
        map((info) => {
          if (!info || !info.seriesInfo) {
            throw new Error('Invalid response from TWC API');
          }

          const productId = twcProduct.defaultProductId;
          const seriesInfo = info.seriesInfo[productId];

          if (
            !seriesInfo ||
            !seriesInfo.series ||
            seriesInfo.series.length === 0
          ) {
            throw new Error(
              `No series data found for product ID: ${productId}`
            );
          }

          // Get the first series (typically the most recent)
          const series = seriesInfo.series[0];
          const times = (series?.fts ?? []).map(String);
          const tsInSeconds = series?.ts?.toString() ?? null;

          // Create time map ensuring all times are valid for this ts
          const timeMap = this.createTimeMap(
            times,
            twcProduct.series === 'Forecast',
            'seconds'
          );

          // Update the time validation service with this data
          if (tsInSeconds) {
            this._timeValidationService.updateTsToFtsMap(tsInSeconds, timeMap);
          }

          // Also store all the series information for future validation
          seriesInfo.series.forEach((seriesItem) => {
            const seriesTs = seriesItem.ts?.toString() ?? '';
            const ftsList = (seriesItem.fts ?? []).map(String);

            if (seriesTs && ftsList.length > 0) {
              this._timeValidationService.updateTsToFtsMap(
                seriesTs,
                this.createTimeMap(
                  ftsList,
                  twcProduct.series === 'Forecast',
                  'seconds'
                )
              );
            }
          });

          return {
            twcProduct,
            tsInSeconds,
            timeMap,
            flightLevelValues,
            updatedTimeInSeconds
          };
        }),
        catchError((error) => {
          this._logger.error('Error fetching TWC Web Tile data:', error);
          return of({
            twcProduct,
            tsInSeconds: 'error',
            timeMap: this.createTimeMap([], twcProduct.series === 'Forecast'),
            flightLevelValues,
            updatedTimeInSeconds
          });
        }),
        shareReplay(1)
      );
  }
}
